import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase, type Gallery } from '@/lib/supabase'
import { fetchGalleryAllImages } from '@/features/gallery/api/imageApi'
import type { GalleryAllImages } from '@/features/gallery/types'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import GalleryImageGrid from '@/features/gallery/components/detail/GalleryImageGrid'
import { ArrowLeft, Save, Copy as CopyIcon, Filter } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { franchiseRegions as REGION_OPTIONS } from '@/lib/constants'

type ReorderItem = {
  gridId: number // unique id for grid ordering (can be synthetic)
  type: 'gallery_photo' | 'job_image'
  sourceId: number // id in source table (gallery_photos.id or gallery_job_images.id)
  image_url: string
  caption?: string | null
  review_status?: 'pending' | 'under_review' | 'approved' | 'rejected'
  franchise_region?: string
  is_included?: boolean
}

async function fetchGallery(id: number): Promise<Gallery> {
  const { data, error } = await supabase.from('galleries').select('*').eq('id', id).single()
  if (error) throw error
  return data!
}

async function fetchAllImages(galleryId: number): Promise<GalleryAllImages[]> {
  return fetchGalleryAllImages(galleryId)
}

export default function GalleryReorderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const galleryId = React.useMemo(() => {
    const n = id ? parseInt(id) : NaN
    return Number.isNaN(n) ? null : n
  }, [id])

  const { data: gallery } = useQuery({
    queryKey: ['gallery', galleryId],
    queryFn: () => fetchGallery(galleryId!),
    enabled: !!galleryId,
  })

  const { data: images = [], isLoading, refetch } = useQuery({
    queryKey: ['gallery-all-images', galleryId],
    queryFn: () => fetchAllImages(galleryId!),
    enabled: !!galleryId,
  })

  // Build reorder items and grid ids
  const items = React.useMemo<ReorderItem[]>(() => {
    if (!images?.length) return []
    return images
      .filter((img) => img.original_id != null && img.image_type != null && img.image_url != null)
      .map((img) => ({
        gridId: img.image_type === 'job_image' ? 1_000_000_000 + img.original_id! : img.original_id!,
        type: img.image_type as 'gallery_photo' | 'job_image',
        sourceId: img.original_id!,
        image_url: img.image_url!,
        caption: img.caption,
        review_status: img.review_status as ReorderItem['review_status'],
        franchise_region: img.franchise_region ?? undefined,
        is_included: img.is_included ?? undefined,
      }))
  }, [images])

  const [orderedIds, setOrderedIds] = React.useState<number[]>([])
  const [initialIds, setInitialIds] = React.useState<number[]>([])
  const [saving, setSaving] = React.useState(false)

  // Filters
  const [typeFilter, setTypeFilter] = React.useState<'all' | 'gallery_photo' | 'job_image'>('all')
  const [statusFilter, setStatusFilter] = React.useState<'any' | 'approved' | 'pending' | 'under_review' | 'rejected'>('any')
  const [regionFilter, setRegionFilter] = React.useState<'all' | string>('all')
  const [includedOnly, setIncludedOnly] = React.useState<boolean>(false)

  const mapById = React.useMemo(() => new Map(items.map(i => [i.gridId, i])), [items])
  const filteredOrdered = React.useMemo(() => {
    const seq = orderedIds.map(id => mapById.get(id)).filter(Boolean) as ReorderItem[]
    return seq.filter(i => {
      if (typeFilter !== 'all' && i.type !== typeFilter) return false
      if (statusFilter !== 'any') {
        // Direct photos have no review status; treat them as approved
        const st = i.type === 'gallery_photo' ? 'approved' : i.review_status
        if (st !== statusFilter) return false
      }
      if (regionFilter !== 'all') {
        const rg = i.franchise_region ?? 'general'
        if (rg !== regionFilter) return false
      }
      if (includedOnly && i.type === 'job_image') {
        if (!i.is_included) return false
      }
      return true
    })
  }, [orderedIds, mapById, typeFilter, statusFilter, regionFilter, includedOnly])

  const copyUrls = async () => {
    try {
      const urls = filteredOrdered.map(i => i.image_url).join('\n')
      await navigator.clipboard.writeText(urls)
      toast({ title: 'Copied', message: `Copied ${filteredOrdered.length} URL(s) to clipboard`, type: 'success' })
    } catch (err) {
      console.error('Copy failed', err)
      toast({ title: 'Copy failed', message: 'Unable to copy URLs', type: 'error' })
    }
  }

  React.useEffect(() => {
    const ids = items.map(i => i.gridId)
    setOrderedIds(ids)
    setInitialIds(ids)
  }, [items])

  const dirty = React.useMemo(() => {
    if (orderedIds.length !== initialIds.length) return true
    for (let i = 0; i < orderedIds.length; i++) if (orderedIds[i] !== initialIds[i]) return true
    return false
  }, [orderedIds, initialIds])

  const saveOrder = async () => {
    if (!galleryId || !orderedIds.length) return
    setSaving(true)
    try {
      // Build updates with a single global order index across both tables
      const map = new Map(items.map(i => [i.gridId, i]))
      const photoUpdates: { id: number; sort_order: number }[] = []
      const jobLinkUpdates: { id: number; sort_order: number }[] = []
      for (let i = 0; i < orderedIds.length; i++) {
        const item = map.get(orderedIds[i])
        if (!item) continue
        if (item.type === 'gallery_photo') photoUpdates.push({ id: item.sourceId, sort_order: i })
        else jobLinkUpdates.push({ id: item.sourceId, sort_order: i })
      }

      for (const u of photoUpdates) {
        const { error } = await supabase.from('gallery_photos').update({ sort_order: u.sort_order }).eq('id', u.id)
        if (error) throw error
      }

      for (const u of jobLinkUpdates) {
        const { error } = await supabase.from('gallery_job_images').update({ sort_order: u.sort_order }).eq('id', u.id)
        if (error) throw error
      }

      toast({ title: 'Order saved', message: 'Photo order has been updated', type: 'success' })
      setInitialIds(orderedIds)
      await refetch()
      navigate(`/media/galleries/${galleryId}`)
    } catch (err) {
      console.error('Failed to save order', err)
      toast({ title: 'Failed to save order', message: 'Please try again', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reorder Photos</h1>
          <p className="text-sm text-muted-foreground">
            {gallery ? gallery.title : 'Loading gallery...'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/media/galleries/${galleryId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Gallery
          </Button>
          <Button onClick={saveOrder} disabled={!dirty || saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : dirty ? 'Save Order' : 'Order Saved'}
          </Button>
          <Button variant="secondary" onClick={copyUrls} disabled={!filteredOrdered.length}>
            <CopyIcon className="w-4 h-4 mr-2" />
            Copy URLs ({filteredOrdered.length})
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Drag to reorder</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 mb-3">
            <p className="text-sm text-muted-foreground">
              Drag photos to set their display order. Use filters to target a group, then click “Copy URLs”.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="w-4 h-4" /> Filters
              </div>
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="gallery_photo">Direct Uploads</SelectItem>
                  <SelectItem value="job_image">Job Images</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={regionFilter} onValueChange={(v) => setRegionFilter(v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {REGION_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Checkbox id="includedOnly" checked={includedOnly} onCheckedChange={(v) => setIncludedOnly(Boolean(v))} />
                <label htmlFor="includedOnly" className="text-sm text-muted-foreground select-none">Included only</label>
              </div>
            </div>
          </div>
          <GalleryImageGrid
            images={items.map(i => ({ id: i.gridId, image_url: i.image_url, caption: i.caption }))}
            loading={isLoading}
            height={600}
            minTileSize={160}
            onOrderChange={setOrderedIds}
          />
        </CardContent>
      </Card>
    </div>
  )
}
